import { T, useLocale } from "../../localization";
import { bookDescription } from "../../localization/book-descriptions";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiBookmark, FiChevronDown, FiHeadphones, FiPlay, FiX } from "react-icons/fi";
import Header from "../../components/header";
import SideMenu from "../../components/sideMenu";
import AudiobookPlayer from "../../components/AudiobookPlayer";
import { track, membershipLevel } from "../../helpers/analytics";
import {
  getAudiobookCatalog,
  getAudiobookMembership,
  getAudiobookLibrary,
  getAudiobookGenres,
  getAudiobookProgress,
  requestAudiobookAccess,
  saveAudiobookProgress,
} from "../../api/rest-apis";
import "./css/audiobooks.css";

const HeadphonesIcon: any = FiHeadphones;
const BookmarkIcon: any = FiBookmark;
const ChevronDownIcon: any = FiChevronDown;
const PlayIcon: any = FiPlay;
const CloseIcon: any = FiX;

interface Genre { id: number; name: string; slug: string; parent: number; heading?: boolean; }
const DEFAULT_GENRE = "series:mmsportsromance";
interface Audiobook {
  id: number; title: string; description: string; cover_url: string; cover_srcset?: string; author: string;
  duration_seconds: number; has_chapters: boolean; genres: Genre[];
  recording_language?: 'en' | 'de';
  series: { id: number; name: string; slug: string }[];
  progress?: { position_seconds: number; preview_seconds_used: number; saved: boolean } | null;
}

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
  return `${Math.floor(seconds / 60)}:${Math.floor(seconds % 60).toString().padStart(2, "0")}`;
};

const Audiobooks = () => {
  const { t, language } = useLocale();
  const [accessPlan, setAccessPlan] = useState("Subscriber");
  const [membershipLoaded, setMembershipLoaded] = useState(!localStorage.getItem("token"));
  useEffect(() => {
    if (!localStorage.getItem("token")) return;
    let mounted = true;
    getAudiobookMembership().then((response: any) => {
      const plan = String(response?.plan || "").toLowerCase();
      if (mounted) setAccessPlan(plan === "premium" ? "Premium" : plan === "plus" || plan === "patron" ? "Patron" : "Subscriber");
    }).catch(() => { if (mounted) setAccessPlan("Membership unavailable"); })
      .finally(() => { if (mounted) setMembershipLoaded(true); });
    return () => { mounted = false; };
  }, []);
  const [playerSession, setPlayerSession] = useState(0);
  const catalogRequest = useRef(0);
  const catalogLanguage = useRef(language);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [books, setBooks] = useState<Audiobook[]>([]);
  const [progress, setProgress] = useState<any[]>([]);
  const [genre, setGenre] = useState("");
  const [showSaved, setShowSaved] = useState(false);
  const [expandedGenres, setExpandedGenres] = useState<Record<number, boolean>>({});
  const [currentBook, setCurrentBook] = useState<Audiobook | null>(null);
  const [currentAccess, setCurrentAccess] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [error, setError] = useState("");
  const [limitMessage, setLimitMessage] = useState("");

  const [expandedDescriptions, setExpandedDescriptions] = useState<Record<number, boolean>>({});
  const menuGenres = genres;
  const topGenres = useMemo(() => menuGenres.filter((item) => !item.parent), [menuGenres]);
  const childGenres = useMemo(() => menuGenres.filter((item) => item.parent), [menuGenres]);
  const tier = !localStorage.getItem("token") ? "guest" : membershipLoaded ? membershipLevel(accessPlan) : "unknown";
  const selection = genres.find(item => item.slug === genre);
  const analyticsContext = { membership_level: tier, menu_id: selection?.id, menu_name: selection?.name,
    parent_menu_id: selection?.parent, source: showSaved ? "library" : "catalog" };
  const bookContext = (book: Audiobook) => ({ ...analyticsContext, book_id: book.id, book_title: book.title });
  const viewTracked = useRef(false);
  useEffect(() => {
    if (membershipLoaded && !viewTracked.current) {
      viewTracked.current = true;
      track("audiobook_access_view", { membership_level: tier,
        access_mode: tier === "premium" ? "full" : tier === "patron" ? "chapter_or_ten_minutes" : tier === "unknown" ? "unknown" : "preview" });
    }
  }, [membershipLoaded, tier]);

  const loadCatalog = async (selectedGenre = genre) => {
    if (!selectedGenre) return;
    const requestId = ++catalogRequest.current;
    setLoading(true);
    setCatalogLoaded(false);
    try {
      const catalog = selectedGenre === "library" ? await getAudiobookLibrary() : await getAudiobookCatalog({ genre: selectedGenre, per_page: 100 });
      if (requestId !== catalogRequest.current) return;
      setBooks(catalog.books || []);
      setCatalogLoaded(true);
      setError("");
    } catch (requestError: any) {
      if (requestId !== catalogRequest.current) return;
      setError(requestError?.message || "The audiobook catalog is unavailable right now.");
    } finally { if (requestId === catalogRequest.current) setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const genreResponse = await getAudiobookGenres();
        const availableGenres: Genre[] = genreResponse.genres || [];
        setGenres(availableGenres);
        if (availableGenres.some(item => item.slug === DEFAULT_GENRE && item.parent && !item.heading)) {
          setGenre(DEFAULT_GENRE);
          void loadCatalog(DEFAULT_GENRE);
        }
      } catch (requestError: any) {
        setError(requestError?.message || "The audiobook catalog is unavailable right now.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    let mounted = true;
    const refresh = () => {
      if (document.hidden || !localStorage.getItem("token") || currentBook) return;
      getAudiobookProgress().then(response => { if (mounted) setProgress(response.progress || []); })
        .catch(() => { if (mounted) setError("Saved listening progress could not load. Please try again."); });
    };
    refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { mounted = false; window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [currentBook]);

  useEffect(() => {
    if (catalogLanguage.current === language) return;
    catalogLanguage.current = language;
    if (showSaved || genre) void loadCatalog(showSaved ? 'library' : genre);
  }, [language, showSaved, genre]);

  const toggleLibrary = () => {
    track("audiobook_library_toggle", { ...analyticsContext, enabled: !showSaved });
    if (!localStorage.getItem("token")) { setError("Sign in to view your audiobooks."); return; }
    const next = !showSaved;
    setShowSaved(next);
    setExpandedGenres({});
    if (next || genre) void loadCatalog(next ? "library" : genre);
    else { ++catalogRequest.current; setBooks([]); setCatalogLoaded(false); setLoading(false); }
  };

  const selectGenre = (selectedGenre: string) => {
    const item = genres.find(value => value.slug === selectedGenre);
    track("audiobook_menu_select", { membership_level: tier, menu_id: item?.id, menu_name: item?.name, parent_menu_id: item?.parent });
    setGenre(selectedGenre);
    setShowSaved(false);
    setExpandedGenres({});
    void loadCatalog(selectedGenre);
  };

  const savedByBook = useMemo(() => {
    const map = new Map<string, any>();
    books.forEach((book) => { if (book.progress) map.set(String(book.id), book.progress); });
    progress.forEach((item) => {
      const existing = map.get(String(item.book_id));
      if (!existing || !existing.updatedAt || !item.updatedAt || new Date(item.updatedAt) >= new Date(existing.updatedAt)) map.set(String(item.book_id), item);
    });
    return map;
  }, [books, progress]);

  const visibleBooks = books.filter((book) => {
    if (!showSaved) return true;
    const item = savedByBook.get(String(book.id));
    return Boolean(item?.saved || item?.position_seconds > 0 || item?.bookmarks?.length);
  });

  const playBook = async (book: Audiobook) => {
    track("audiobook_play_click", bookContext(book));
    if (!localStorage.getItem("token")) { track("audiobook_access_denied", { ...bookContext(book), reason: "sign_in_required" }); setError("Please sign in to listen to audiobook previews."); return; }
    setPlayerLoading(true); setError("");
    setCurrentBook(null);
    try {
      const access = await requestAudiobookAccess(book.id);
      if (!access.stream_url) throw new Error("The audiobook service did not return an audio stream.");
      const context = { ...bookContext(book), access_mode: access.access_mode };
      track("audiobook_access_granted", context);
      setCurrentAccess({ ...access, analytics: context, progress: access.progress || savedByBook.get(String(book.id)) });
      setPlayerSession(value => value + 1);
      setCurrentBook(book);
    } catch (requestError: any) {
      track("audiobook_access_denied", { ...bookContext(book), reason: requestError?.code === "PREMIUM_REQUIRED" ? "premium_required" : "request_failed" });
      if (requestError?.code === "PREMIUM_REQUIRED") setLimitMessage("Become a Premium member to listen to the full audiobook.");
      else setError(requestError?.message || "This audiobook could not be started.");
    } finally { setPlayerLoading(false); }
  };

  const toggleSaved = async (book: Audiobook) => {
    if (!localStorage.getItem("token")) { setError("Sign in to save audiobooks and resume them across devices."); return; }
    const existing = savedByBook.get(String(book.id));
    try {
      const response: any = await saveAudiobookProgress(book.id, { saved: !existing?.saved });
      track("audiobook_saved", { ...bookContext(book), enabled: Boolean(response.progress?.saved) });
      setProgress((items) => [...items.filter((item) => String(item.book_id) !== String(book.id)), response.progress]);
    } catch (requestError: any) { setError(requestError?.message || "Unable to save this audiobook."); }
  };

  return <div className="audiobooks-page">
    <SideMenu />
    <main className="audiobooks-main">
      <Header />
      <section className="audiobooks-heading"><div>
        <p className="audiobooks-kicker"><HeadphonesIcon /> Bookish Boyfriend Audio</p>
        <h2><T text="Audiobooks" /></h2>
        <p><T text="Listen to previews as a subscriber, Chapter One as a Patron, or the full library with Premium." /></p>
      </div><div className="audiobooks-access-row"><div className="audiobooks-access-note"><strong><T text={accessPlan === "Membership unavailable" ? accessPlan : `${accessPlan} access`} /></strong><span><T text={accessPlan === "Premium" ? "Full audiobooks and synced listening across devices." : accessPlan === "Patron" ? "Previews and Chapter One, or 10 minutes for books without chapters." : "Audiobook previews. Upgrade to Premium for full audiobooks."} /></span></div><button className={`audiobook-filter audiobook-saved-mobile${showSaved ? " active" : ""}`} aria-pressed={showSaved} disabled={loading} onClick={toggleLibrary}><BookmarkIcon /> <T text="My Audiobooks" /></button></div></section>

      <section className="audiobooks-toolbar" aria-label={t("Audiobook filters")}>
        <button className={showSaved ? "audiobook-filter active" : "audiobook-filter"} aria-pressed={showSaved} disabled={loading} onClick={toggleLibrary}><BookmarkIcon /> <T text="My Audiobooks" /></button>
      </section>

      <section className="audiobook-categories" aria-label={t("Audiobook genres")}>
        {topGenres.map((item) => { const children = childGenres.filter((child) => child.parent === item.id); const expanded = expandedGenres[item.id]; return <div className="category-group" key={item.id}>
          <button aria-expanded={Boolean(expanded)} className={children.some(child => child.slug === genre && genre) ? "category-pill active" : "category-pill"} onClick={() => { track("audiobook_menu_toggle", { membership_level: tier, menu_id: item.id, menu_name: item.name, enabled: !expanded }); setExpandedGenres({ [item.id]: !expanded }); }}><span><T text={item.name} /></span><ChevronDownIcon className={expanded ? "rotated" : ""} /></button>
          {expanded && <div className="subcategory-list">{children.map((child) => child.heading ? <span className="subcategory-heading" key={child.id}><T text={child.name} /></span> : <button key={child.id} className={genre === child.slug ? "subcategory active" : "subcategory"} onClick={() => selectGenre(child.slug)}><T text={child.name} /></button>)}</div>}
        </div>; })}
      </section>

      {error && <div className="audiobook-error"><T text={error} /></div>}
      {(genre || showSaved) && <h3 className="audiobook-category-title"><T text={showSaved ? "My Audiobooks" : genres.find(item => item.slug === genre)?.name} /></h3>}
      {!catalogLoaded && !loading ? <div className="audiobook-empty audiobook-selection-prompt"><HeadphonesIcon /><strong><T text="Browse by genre" /></strong></div> : loading ? <div className="audiobook-empty"><T text="Loading audiobooks..." /></div> : visibleBooks.length === 0 ? <div className="audiobook-empty"><T text={showSaved ? "Your saved audiobooks will appear here." : "No audiobooks found in this category."} /></div> : <section className="audiobook-grid">
        {visibleBooks.map((book) => { const saved = savedByBook.get(String(book.id)); return <article className="audiobook-card" key={book.id}>
          <img loading="lazy" decoding="async" src={book.cover_url} srcSet={book.cover_srcset || undefined} sizes="300px" alt={language === "de" ? `Buchcover: ${book.title}` : `${book.title} cover`} /><div className="audiobook-card-body"><div className="audiobook-card-title"><h3>{book.title}</h3><button title={t("Save audiobook")} className={saved?.saved ? "save-audiobook active" : "save-audiobook"} onClick={() => toggleSaved(book)}><BookmarkIcon /></button></div>
            {language === 'de' && book.recording_language && <p className="audiobook-meta">Hörbuchsprache: {book.recording_language === 'de' ? 'Deutsch' : 'Englisch'}</p>}
            {book.series?.[0] && <p className="audiobook-series">{book.series[0].name}</p>}<p className={`audiobook-description${expandedDescriptions[book.id] ? " expanded" : ""}`}>{bookDescription(book.title, book.description, language)}</p>{book.description && <button className="audiobook-see-more" aria-expanded={Boolean(expandedDescriptions[book.id])} onClick={() => setExpandedDescriptions(value => ({ ...value, [book.id]: !value[book.id] }))}><T text={expandedDescriptions[book.id] ? "See less" : "See more"} /></button>}<div className="audiobook-meta">{book.duration_seconds > 0 && <span>{formatTime(book.duration_seconds)}</span>}</div>{saved?.position_seconds > 0 && <p className="resume-copy"><T text="Resume at" /> {formatTime(saved.position_seconds)}</p>}<button className="play-audiobook" onClick={() => playBook(book)} disabled={playerLoading}><PlayIcon /> <T text={playerLoading ? "Starting..." : "Preview / Play"} /></button>
          </div></article>; })}
      </section>}
    </main>
    {currentBook && <AudiobookPlayer key={playerSession} book={currentBook} access={currentAccess} onClose={() => setCurrentBook(null)} onLimit={setLimitMessage} onProgress={item => setProgress(items => [...items.filter(value => String(value.book_id) !== String(item.book_id)), item])} />}
    {limitMessage && <div className="audiobook-modal-backdrop"><div className="audiobook-modal"><button title={t("Close")} onClick={() => setLimitMessage("")}><CloseIcon /></button><HeadphonesIcon /><h3><T text="Premium audiobook access" /></h3><p><T text={limitMessage} /></p><Link to="/price" onClick={() => { track("audiobook_upgrade_click", analyticsContext); setLimitMessage(""); }}><T text="View plans" /></Link></div></div>}
  </div>;
};

export default Audiobooks;
